import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type {
  AccountUsageSnapshot,
  MetricAlert,
  MetricsHistoryPoint,
  PanelUser,
  ServerMetrics
} from "../types";

function Bar({
  label,
  percent,
  color
}: {
  label: string;
  percent: number;
  color: string;
}) {
  const p = Math.min(100, Math.max(0, percent));
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 13,
          marginBottom: 4
        }}
      >
        <span>{label}</span>
        <span className="mono faint">{p.toFixed(1)}%</span>
      </div>
      <div
        style={{
          height: 10,
          borderRadius: 6,
          background: "rgba(255,255,255,0.06)",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${p}%`,
            height: "100%",
            background: color,
            borderRadius: 6,
            transition: "width 0.3s"
          }}
        />
      </div>
    </div>
  );
}

function Sparkline({
  points,
  getValue,
  color
}: {
  points: MetricsHistoryPoint[];
  getValue: (p: MetricsHistoryPoint) => number;
  color: string;
}) {
  if (points.length < 2) {
    return <p className="faint">Collect metrics a few times for a chart</p>;
  }
  const vals = points.map(getValue);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const w = 280;
  const h = 56;
  const coords = vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * w;
      const y = h - ((v - min) / (max - min || 1)) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={coords}
      />
    </svg>
  );
}

export function MetricsPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? provisioned[0]?.id || ""
      : currentUser.provisionStatus === "ready"
        ? currentUser.id
        : ""
  );
  const [metrics, setMetrics] = useState<ServerMetrics | null>(null);
  const [history, setHistory] = useState<MetricsHistoryPoint[]>([]);
  const [alerts, setAlerts] = useState<MetricAlert[]>([]);
  const [usage, setUsage] = useState<AccountUsageSnapshot | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const m = await api<{ metrics: ServerMetrics }>("/api/v1/metrics");
      setMetrics(m.metrics);
      const h = await api<{ items: MetricsHistoryPoint[] }>(
        "/api/v1/metrics/history"
      );
      setHistory(h.items || []);
      if (staff) {
        try {
          const a = await api<{ alerts: MetricAlert[] }>(
            "/api/v1/metrics/alerts"
          );
          setAlerts(a.alerts || []);
        } catch {
          setAlerts([]);
        }
      }
      if (userId) {
        const u = await api<{ usage: AccountUsageSnapshot }>(
          `/api/v1/metrics/usage?userId=${encodeURIComponent(userId)}`
        );
        setUsage(u.usage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [staff, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Metrics</h1>
          <p>
            Server CPU / RAM / load / disk · account disk usage · admin alerts
          </p>
        </div>
        <div className="topbar-actions">
          {staff && provisioned.length ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Accounts
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {staff && alerts.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          {alerts.map((a) => (
            <div
              key={a.id}
              className={
                a.severity === "critical" ? "error-banner" : "success-banner"
              }
              style={
                a.severity === "warning"
                  ? {
                      background: "rgba(232,164,92,0.15)",
                      borderColor: "rgba(232,164,92,0.4)",
                      color: "#f5d0a9"
                    }
                  : undefined
              }
            >
              <strong>{a.title}</strong> — {a.message}
            </div>
          ))}
        </div>
      ) : null}

      {metrics ? (
        <div className="grid">
          <section className="card">
            <div className="card-label">Host</div>
            <div className="card-value" style={{ fontSize: 18 }}>
              {metrics.hostname}
            </div>
            <p className="card-meta">
              {metrics.platform} · uptime{" "}
              <span className="mono">
                {Math.floor(metrics.uptimeSec / 3600)}h
              </span>
            </p>
          </section>
          <section className="card">
            <div className="card-label">Load average</div>
            <div className="card-value mono" style={{ fontSize: 20 }}>
              {metrics.load.one} / {metrics.load.five} / {metrics.load.fifteen}
            </div>
            <p className="card-meta">{metrics.cpu.cores} cores</p>
          </section>
          <section className="card">
            <div className="card-label">CPU</div>
            <Bar
              label={
                metrics.cpu.usagePercent != null
                  ? "Usage"
                  : "Usage (sample again)"
              }
              percent={metrics.cpu.usagePercent ?? 0}
              color="#6EE7B7"
            />
            <p className="card-meta faint" style={{ fontSize: 12 }}>
              {metrics.cpu.model.slice(0, 48)}
            </p>
          </section>
          <section className="card">
            <div className="card-label">Memory</div>
            <Bar
              label="RAM"
              percent={metrics.memory.usedPercent}
              color="#E8A45C"
            />
            <p className="card-meta mono">
              {(metrics.memory.usedBytes / 1e9).toFixed(1)} /{" "}
              {(metrics.memory.totalBytes / 1e9).toFixed(1)} GB
            </p>
          </section>
        </div>
      ) : null}

      {metrics?.disk?.length ? (
        <div className="card full" style={{ marginTop: 16 }}>
          <div className="card-label">Disk</div>
          {metrics.disk.map((d) => (
            <Bar
              key={d.path}
              label={d.path}
              percent={d.usedPercent}
              color={
                d.usedPercent >= 95
                  ? "#f87171"
                  : d.usedPercent >= 85
                    ? "#E8A45C"
                    : "#7dd3fc"
              }
            />
          ))}
        </div>
      ) : null}

      <div className="grid" style={{ marginTop: 16 }}>
        <section className="card wide">
          <div className="card-label">Memory history</div>
          <Sparkline
            points={history}
            getValue={(p) => p.memPercent}
            color="#E8A45C"
          />
        </section>
        <section className="card wide">
          <div className="card-label">Disk history</div>
          <Sparkline
            points={history}
            getValue={(p) => p.diskPercent}
            color="#7dd3fc"
          />
        </section>
        <section className="card wide">
          <div className="card-label">Load history</div>
          <Sparkline
            points={history}
            getValue={(p) => p.load1}
            color="#6EE7B7"
          />
        </section>
      </div>

      {usage ? (
        <div className="card full" style={{ marginTop: 16 }}>
          <div className="card-label">Account usage · {usage.username}</div>
          <p className="card-meta">
            Disk{" "}
            <span className="mono">{usage.diskUsedMb} MB</span>
            {" · "}
            inodes <span className="mono">{usage.inodeUsed}</span>
            {" · "}
            <span className="mono faint">{usage.homePath}</span>
          </p>
        </div>
      ) : null}

      {metrics?.services ? (
        <div className="card full" style={{ marginTop: 16 }}>
          <div className="card-label">Services (detected tools)</div>
          <div className="topbar-actions" style={{ flexWrap: "wrap" }}>
            {metrics.services.map((s) => (
              <span
                key={s.name}
                className={`badge ${
                  s.status === "ok" ? "status-active" : "status-suspended"
                }`}
              >
                {s.name}: {s.status}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
